import styled from 'styled-components';

/**
 * The settings dialog's shared visual vocabulary: type scale, row schema, and
 * the small form controls every pane composes.
 */

/**
 * Hierarchy comes from size and colour first, weight last: semibold belongs to
 * headings only. Absolute px so help text cannot compound off a parent's size.
 */
export const TYPE = {
  heading: 'font-size: 15px; font-weight: 600;',
  label: 'font-size: 13px; font-weight: 500;',
  body: 'font-size: 12px; font-weight: 400;',
  meta: 'font-size: 11px; font-weight: 400;',
} as const;

export const PaneTitle = styled.h3`
  margin: 0 0 4px;
  /* Holds its height in PersonalizationView's flex column; inert in the
     dialog, whose container is block. */
  flex-shrink: 0;
  ${TYPE.heading}
  color: var(--text-primary);
`;

export const Row = styled.div`
  display: flex;
  align-items: flex-start;
  /* Same: a setting row keeps its natural height when the editor below it
     takes the leftover space. */
  flex-shrink: 0;
  justify-content: space-between;
  /*
    The control drops under its label rather than being squeezed beside it —
    decided by THIS ROW's width, not the viewport's.

    It used to be "@media (max-width: 640px)", which is the wrong thing to
    measure for an embedded terminal: Paradex and Paradigm mount the pane in a
    drawer a few hundred px wide inside a full-size window, so the query never
    fired. The row stayed horizontal, and because RowText is free to shrink
    while FieldColumn is not, the description column absorbed the whole
    shortfall — help text down to one word per line beside a control that had
    taken the rest. The Credentials pane showed it worst, its secret field
    being the widest control in the dialog.

    "flex-wrap" rather than a container query because it needs no containment
    context on an ancestor: these rows compose under three different parents
    (the settings dialog, the Apps & Skills credentials tab, the
    personalization frame), and a query would need every one of them to declare
    "container-type" or silently keep the old behaviour.

    Wrapping is decided on the items' BASE sizes, before any shrinking, so the
    break lands at RowText's 300px basis + this gap + the control's natural
    width — around 580px, close to the 640px the media query intended.

    Two gaps: the 24px column gap between label and control is unchanged, while
    the row gap is the 10px the stacked layout wants.
  */
  flex-wrap: wrap;
  gap: 10px 24px;
  padding: 16px 0;
  border-bottom: 1px solid var(--divider-color);
`;

export const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  /*
    The schema's description column: 300px preferred, free to shrink.

    Sized rather than left to size itself, because "as wide as the text
    happens to be" is not a column — it made every row's help text a
    different width, and on the widest control cluster (the User Config row's
    dropdown + download + Import) it squeezed the description to ~175px, six
    ragged lines against a control that had taken the rest.

    "1 1 300px", not a fixed 300: the dialog's pane is ~510px inside a 720px
    card, so a rigid 300 plus a 220px control cannot both fit and the control
    would overflow. This claims 300 where there is room and yields where there
    isn't, which is the same bargain the rest of the dialog's flex layout
    makes.
  */
  flex: 1 1 300px;
`;

export const RowLabel = styled.div`
  ${TYPE.label}
  color: var(--text-primary);
`;

export const RowHelp = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: var(--text-secondary);
  max-width: 46ch;
`;

export const FieldColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
  min-width: 0;
  /* "flex-shrink: 0" keeps the controls at their natural width beside a label,
     but once the column has wrapped onto its own line that same rigidity would
     overflow a panel narrower than the control itself (a 260px secret field in
     a 220px dock). Capping at the line width lets each control's own
     "max-width: 100%" carry it the rest of the way down. */
  max-width: 100%;
`;

export const FieldRow = styled.form`
  display: flex;
  align-items: center;
  /* Input + button: the button drops below the field rather than off the edge
     when the column is too narrow to seat both. */
  flex-wrap: wrap;
  gap: 8px;
`;

export const TextInput = styled.input`
  width: 220px;
  max-width: 100%;
  height: 32px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: var(--input-bg-color);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 12px;

  &:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: -1px;
  }

  &[aria-invalid='true'] {
    border-color: var(--error-color);
  }

  /* While focused AND invalid the accent ring would sit on top of the red
     border and hide it — exactly when the user most needs to see it. */
  &[aria-invalid='true']:focus-visible {
    outline-color: var(--error-color);
  }

  &:read-only {
    /* Read-only, not disabled: the value is still selectable and copyable,
       which is the whole point of showing it when it can't be written. */
    color: var(--text-secondary);
    cursor: default;
  }
`;

export const SaveButton = styled.button`
  flex-shrink: 0;
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 6px;
  background: var(--accent-color);
  color: var(--cta-text-color);
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    /* Dimmed rather than hidden: a Save button that appears only once the field
       is dirty makes the row jump as you type. */
    opacity: 0.45;
    cursor: default;
  }
`;

/** The resting half of an Edit ⇄ Save pair. */
export const GhostButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--hover-bg);
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: 2px;
  }
`;

/** Reserves its line so the row does not shift when a message appears. */
export const FieldStatus = styled.div`
  min-height: 16px;
  font-size: 11px;
  line-height: 16px;
  color: var(--text-label);
`;

export const FieldError = styled.span`
  color: var(--error-color);
`;

export const StaticValue = styled.div`
  flex-shrink: 0;
  max-width: 240px;
  font-size: 12px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Notice = styled.div`
  margin-top: 16px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: rgba(var(--warning-color-rgb), 0.08);
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.5;
`;

export const MONO_FONT = `'JetBrains Mono', var(--font-mono, monospace)`;

export const Mono = styled.code`
  font-family: ${MONO_FONT};
  font-size: 11px;
  color: var(--text-primary);
`;
